import { useState } from "react";
import Button from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Chip, DataTable, Drawer, IconButton, StatusGlyph } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  Clock01Icon,
  ShoppingBag01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import Summary from "components/common/Summary";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  type ContactSubscription,
  type InvoiceStatus,
  type SubscriptionCatalogItem,
  type SubscriptionInvoice,
} from "utils/contacts/recurringBilling";
import {
  CADENCE_LABEL_KEY,
  CADENCE_SUFFIX_KEY,
  money,
  shortDate,
} from "utils/contacts/billingFormat";
import SubscriptionEditForm, {
  useSubscriptionEditForm,
  type SubscriptionEditValues,
} from "./SubscriptionEditForm";

interface SubscriptionDetailsDrawerProps {
  open: boolean;
  subscription: ContactSubscription | null;
  /** Who the plan bills, which the subscription record does not carry. */
  customerName: string;
  onClose: () => void;
  onSave: (values: SubscriptionEditValues) => void;
  onPause: () => void;
  onCancel: () => void;
}

const INVOICE_STATUS: Record<
  InvoiceStatus,
  { key: string; tone: "positive" | "info" | "negative" }
> = {
  PAID: { key: "Paid", tone: "positive" },
  OPEN: { key: "Open", tone: "info" },
  FAILED: { key: "Failed", tone: "negative" },
};

type DetailTab = "history" | "catalog";

type Step = "view" | "edit";

const SubscriptionDetailsDrawer: React.FC<SubscriptionDetailsDrawerProps> = ({
  open,
  subscription,
  customerName,
  onClose,
  onSave,
  onPause,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<DetailTab>("history");
  const [step, setStep] = useState<Step>("view");

  const editForm = useSubscriptionEditForm(subscription, (values) => {
    onSave(values);
    setStep("view");
  });

  // Leaving the drawer leaves the form: reopening on a half-edited plan the
  // caller has since swapped is worse than starting from the record.
  const close = () => {
    setStep("view");
    editForm.resetForm();
    onClose();
  };

  const leaveEditStep = () => {
    setStep("view");
    editForm.resetForm();
  };

  // Nothing to describe, so nothing to draw. The drawer still mounts, so its
  // open/close transition is the DS's rather than a remount.
  if (!subscription) {
    return (
      <Drawer
        open={open}
        onClose={close}
        title={t("Subscription Details")}
        width={520}
      >
        <span />
      </Drawer>
    );
  }

  const canceled = subscription.status === "CANCELED";

  const invoiceColumns: DataColumn<SubscriptionInvoice>[] = [
    {
      id: "issuedAt",
      header: t("Date"),
      cell: (invoice) => shortDate(invoice.issuedAt, i18n.language),
    },
    {
      id: "number",
      header: t("Invoice"),
      cell: (invoice) => invoice.number,
    },
    {
      id: "status",
      header: t("Status"),
      cell: (invoice) => {
        const { key, tone } = INVOICE_STATUS[invoice.status];
        return (
          <Chip
            size="sm"
            tone={tone}
            leading={<StatusGlyph status={key} tone={tone} />}
          >
            {t(key)}
          </Chip>
        );
      },
    },
    {
      id: "amount",
      header: t("Amount"),
      align: "right",
      cell: (invoice) => money(invoice.amountCents, 2, i18n.language),
    },
  ];

  const catalogColumns: DataColumn<SubscriptionCatalogItem>[] = [
    {
      id: "product",
      header: t("Product/Service"),
      cell: (item) => item.productName,
    },
    {
      id: "quantity",
      header: t("Qty"),
      align: "right",
      cell: (item) => String(item.quantity),
    },
    {
      id: "total",
      header: t("Total"),
      align: "right",
      cell: (item) => money(item.totalCents, 2, i18n.language),
    },
  ];

  const summaryItems = [
    { title: t("Customer"), value: customerName },
    { title: t("Billing"), value: t(CADENCE_LABEL_KEY[subscription.cadence]) },
    {
      title: t("Next Invoice"),
      value: subscription.nextChargeAt
        ? shortDate(subscription.nextChargeAt, i18n.language)
        : t("Not scheduled"),
    },
    {
      title: t("Started"),
      value: shortDate(subscription.startedAt, i18n.language),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={close}
      title={t("Subscription Details")}
      footer={
        step === "edit" ? (
          <Inline
            gap="var(--space-4)"
            align="center"
            justify="end"
            wrap={false}
          >
            {/* Submits the form by id, so the same Enter key that submits a
                focused field also drives the footer button. */}
            <Button
              variant="primary-filled"
              onClick={() => editForm.handleSubmit()}
              // Nothing changed, so there is nothing to save. Leaving it live
              // invites a write that says "saved" and altered nothing.
              disabled={!editForm.dirty}
            >
              {t("Save")}
            </Button>
          </Inline>
        ) : (
          <Inline
            gap="var(--space-4)"
            align="center"
            justify="center"
            wrap={false}
          >
            <Button variant="default-soft" onClick={close} fullWidth>
              {t("Exit")}
            </Button>
          </Inline>
        )
      }
    >
      {step === "edit" ? (
        <Stack gap="var(--space-5)">
          <Inline gap="var(--space-6)" align="center" wrap={false}>
            <IconButton
              variant="ghost"
              size="small"
              aria-label={t("Back to subscription details")}
              onClick={leaveEditStep}
            >
              <HugeiconsIcon icon={ArrowLeft01Icon} size={18} />
            </IconButton>
            <Typography variant="h5">{t("Edit Subscription")}</Typography>
          </Inline>

          <SubscriptionEditForm formik={editForm} />
        </Stack>
      ) : (
        <Stack gap="var(--space-6)">
          <Stack gap="var(--space-5)">
            <Inline justify="between" align="center">
              <Typography variant="subtitle2">
                {subscription.planName}
              </Typography>
              <Chip
                size="sm"
                tone={canceled ? "negative" : "positive"}
                leading={
                  <StatusGlyph
                    status={canceled ? "Canceled" : "Active"}
                    tone={canceled ? "negative" : "positive"}
                  />
                }
              >
                {canceled ? t("Canceled") : t("Active")}
              </Chip>
            </Inline>

            <Typography variant="h2" component="p">
              {money(subscription.amountCents, 2, i18n.language)}
              <Typography component="span" variant="subtitle2">
                {t(CADENCE_SUFFIX_KEY[subscription.cadence])}
              </Typography>
            </Typography>

            <Summary items={summaryItems} disableContainer alignEndColumn />

            <Inline
              gap="var(--space-4)"
              align="center"
              justify="center"
              wrap={false}
            >
              <Button
                variant="primary-filled"
                size="small"
                onClick={() => setStep("edit")}
                fullWidth
              >
                {t("Edit")}
              </Button>
              {/* A canceled plan has nothing left to pause or cancel. */}
              <Button
                variant="primary-outlined"
                size="small"
                onClick={onPause}
                disabled={canceled}
                fullWidth
              >
                {t("Pause")}
              </Button>
              <Button
                variant="primary-outlined"
                size="small"
                onClick={onCancel}
                disabled={canceled}
                fullWidth
              >
                {t("Cancel")}
              </Button>
            </Inline>
          </Stack>

          <Stack gap="var(--space-4)">
            <Tabs
              value={activeTab}
              onChange={(_event, value: DetailTab) => setActiveTab(value)}
              aria-label={t("Subscription Details")}
              variant="fullWidth"
            >
              <Tab
                value="history"
                label={t("Billing History")}
                icon={<HugeiconsIcon icon={Clock01Icon} size={16} />}
                iconPosition="start"
              />
              <Tab
                value="catalog"
                label={t("Catalog item")}
                icon={<HugeiconsIcon icon={ShoppingBag01Icon} size={16} />}
                iconPosition="start"
              />
            </Tabs>

            {activeTab === "history" && (
              <Stack gap="var(--space-3)">
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-tertiary)" }}
                >
                  {t("BillingHistoryIntro")}
                </Typography>
                <DataTable<SubscriptionInvoice>
                  columns={invoiceColumns}
                  rows={subscription.invoices}
                  getRowKey={(invoice) => invoice.id}
                  emptyState={t("This plan has not billed yet")}
                />
              </Stack>
            )}

            {activeTab === "catalog" && (
              <Stack gap="var(--space-3)">
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-tertiary)" }}
                >
                  {subscription.catalogItem.description}
                </Typography>
                <DataTable<SubscriptionCatalogItem>
                  columns={catalogColumns}
                  rows={[subscription.catalogItem]}
                  getRowKey={(item) => item.productName}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
};

export default SubscriptionDetailsDrawer;
